import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, Lock, Plus, Shuffle } from "lucide-react";
import { isFeatureAllowed } from "@lucralink/shared";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { RotatorCard } from "@/components/rotator/RotatorCard";
import { RotatorWizard } from "@/components/rotator/RotatorWizard";
import { useEntitlements } from "@/lib/entitlements";
import { useTenant } from "@/lib/hooks/useTenant";
import { useRotators, type Rotator } from "@/lib/hooks/useRotators";
import { MAX_ROTATOR_GROUPS, WHATSAPP_GROUP_LIMIT } from "@/lib/rotators";

const SUBTITLE = `Um link só que distribui cada clique para o próximo grupo de WhatsApp da lista, até ${MAX_ROTATOR_GROUPS} grupos.`;

function LockedState(): JSX.Element {
  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader title="Rodízio de grupos" subtitle={SUBTITLE} />
      <div className="rounded-xl border border-dashed border-border bg-[hsl(var(--surface)/0.5)] p-12 text-center space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Lock size={24} className="text-primary" />
        </div>
        <p className="font-semibold text-foreground">O Rodízio de grupos é um recurso do plano PRO</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          Quando um grupo de WhatsApp chega perto de {WHATSAPP_GROUP_LIMIT} membros, o rodízio manda os próximos cliques para os outros grupos, sem você trocar o link divulgado.
        </p>
        <Button asChild variant="gold">
          <Link to="/app/plans">Ver planos</Link>
        </Button>
      </div>
    </div>
  );
}

type Editing = { readonly mode: "closed" } | { readonly mode: "new" } | { readonly mode: "edit"; readonly rotator: Rotator };

export function RodizioPage(): JSX.Element {
  const { data: billing } = useTenant();
  const entitlements = useEntitlements(billing?.tenant);
  const { data: rotators = [], isLoading, error } = useRotators();
  const [editing, setEditing] = useState<Editing>({ mode: "closed" });

  if (!isFeatureAllowed(entitlements, "groupRotator")) {
    return <LockedState />;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader
        title="Rodízio de grupos"
        subtitle={SUBTITLE}
        actions={
          editing.mode === "closed" && (
            <Button onClick={() => setEditing({ mode: "new" })}>
              <Plus size={16} /> Novo rodízio
            </Button>
          )
        }
      />
      {editing.mode !== "closed" && (
        <RotatorWizard key={editing.mode === "edit" ? editing.rotator.id : "new"} initial={editing.mode === "edit" ? editing.rotator : null} onClose={() => setEditing({ mode: "closed" })} />
      )}
      {error && (
        <div role="alert" className="flex items-center gap-3 rounded-lg border border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.08)] px-4 py-3 text-sm text-[hsl(var(--destructive))]">
          <AlertCircle size={16} className="shrink-0" />
          <span>Não foi possível carregar os rodízios: {error.message}</span>
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando rodízios...</p>
      ) : rotators.length === 0 && editing.mode === "closed" ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border-strong))] p-10 text-center space-y-3">
          <Shuffle size={28} className="mx-auto text-muted-foreground" />
          <p className="font-medium text-foreground">Nenhum rodízio criado</p>
          <p className="mx-auto max-w-md text-sm text-muted-foreground">
            Escolha os grupos em que o seu número é administrador, defina a ordem e divulgue um link só. Prévias de link do WhatsApp não contam como clique.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {rotators.map((r) => (
            <RotatorCard key={r.id} rotator={r} onEdit={() => setEditing({ mode: "edit", rotator: r })} />
          ))}
        </div>
      )}
    </div>
  );
}
